import React, { useCallback, useMemo } from 'react';

import {
  ColumnsIcon, LinkExternalIcon, PlayIcon, XCircleIcon
} from '@primer/octicons-react';

import {
  formatSequencedBarChartDatasets,
  getBarChartComparisonDatasets,
} from '../../charts/helpers/dataset.helpers';

import {
  deriveAssayChartDatasetsByStatus,
  mapAssayBarData,
  mapAssayBarLabels,
  mapAssayPointData,
} from '../../../utils/assay_utils';
import changeAssayStatus from '../../../utils/api_functions/changeAssayStatus';

import { assayScoreFormatter } from '../../../utils/formatters';
import { formatChartPointDate } from '../../../utils/date_utils';

import ChartCard from '../../charts/ChartCard/ChartCard';
import { ChartProvider } from '../../charts/ChartContext';

export const AssayResultBarChart = ({
  assayName,
  assayRunResult,
  assayDetails,
  showDetailsFor,
  onClose,
  onStatusToggle,
  showIconMenu = false,
  isLoading = false,
  hideNormalDropdownOptions = false,
  hideCardTitle = false,
  message = null,
}) => {
  const assayStatus = assayDetails?.active;

  const normalModalOptions = [
    {
      id: 'changeAssayStatus',
      label: assayStatus ? 'Pause Assay' : 'Resume Assay',
      icon: assayStatus ? ColumnsIcon : PlayIcon,
      onClick: () => onStatusToggle(),
    },
    {
      id: 'showDetails',
      label: 'Show Assay Details',
      icon: LinkExternalIcon,
      onClick: () => showDetailsFor(assayDetails),
    },
  ]

  const dropdownOptions = [
    ...(hideNormalDropdownOptions ? [] : normalModalOptions),
    {
      id: 'close',
      label: 'Go back',
      icon: XCircleIcon,
      onClick: () => onClose(),
    },
  ]

  const chartID = assayDetails?.id;
  const chartDatasets = useMemo(() => {
    const barData = mapAssayBarData(assayRunResult);
    return getBarChartComparisonDatasets(barData?.baseline, barData?.window);
  }, [assayRunResult]);

  const labels = useMemo(() => mapAssayBarLabels(assayRunResult), [assayRunResult])
  const metric = assayRunResult?.summarizerMetadata?.metric;
  const score = assayRunResult?.score;

  const chartTitle = useMemo(() => hideCardTitle ? '' : `${assayName} (${metric} = ${assayScoreFormatter.format(score)})`, [assayName, hideCardTitle, metric, score])

  return (
    <ChartProvider
      chartID={`${chartID}-run-details`}
      chartType="bar"
      chartConfiguration="bar"
      datasets={chartDatasets}
      labels={labels}
      dropdownOptions={dropdownOptions}
      prepareDataset={formatSequencedBarChartDatasets}
      usePaddedMax
    >
      <ChartCard
        className="h-chart-card"
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        onClose={onClose}
        minHeight="200px"
        disableFullHeight
        showIconBasedMenu={showIconMenu}
      />
    </ChartProvider>
  )
}

export const AssayWindowPointChart = ({
  onPointClick,
  assayName,
  assayDetails,
  assayRunResults = [],
  showDetailsFor,
  onStatusToggle,
  isLoading = false,
  showIconMenu = false,
  hideNormalDropdownOptions = false,
  hideCardTitle = false,
  message = null,
  loadingText = null,
}) => {
  const assayStatus = assayDetails?.active;

  const dropdownOptions = hideNormalDropdownOptions ? [] : [
    {
      id: 'changeAssayStatus',
      label: assayStatus ? 'Pause Assay' : 'Resume Assay',
      icon: assayStatus ? ColumnsIcon : PlayIcon,
      onClick: () => onStatusToggle(),
    },
    {
      id: 'showDetails',
      label: 'Show Assay Details',
      icon: LinkExternalIcon,
      onClick: () => showDetailsFor(assayDetails),
    },
  ]

  const chartID = assayDetails?.id;
  const threshold = assayDetails?.alertThreshold ?? assayDetails?.alert_threshold;

  const assayPoints = useMemo(() => mapAssayPointData(assayRunResults), [assayRunResults])
  const assayDatasets = useMemo(() => deriveAssayChartDatasetsByStatus(assayName, assayPoints), [assayName, assayPoints])

  const thresholdLineDataset = {
    type: 'line',
    label: 'Threshold',
    showLine: true,
    data: new Array(assayRunResults.length).fill(threshold),
    pointStyle: 'line',
    spanGaps: true,
    backgroundColor: 'rgba(200,0,0,0.5)',
    borderColor: 'red',
    borderWidth: 1,
    // fill: true,
  }
  const labels = assayPoints?.map(({ x }) => formatChartPointDate(x));

  return (
    <ChartProvider
      chartID={`${chartID}-history`}
      chartType="line"
      chartConfiguration="assay"
      datasets={[...assayDatasets, thresholdLineDataset]}
      labels={labels}
      dropdownOptions={dropdownOptions}
      usePaddedMax
    >
      <ChartCard
        className="h-chart-card"
        chartTitle={hideCardTitle ? '' : assayName}
        isLoading={isLoading}
        message={message}
        minHeight="200px"
        onPointClick={onPointClick}
        disableFullHeight
        showIconBasedMenu={showIconMenu}
        loadingText={loadingText}
      />
    </ChartProvider>
  )
}


export const ModalAssayChart = ({
  assayRunResults,
  assayDetails,
  assayName,
  showDetailsFor,
  selectedRun,
  setSelectedRun,
  refetchAssays,
  isLoading = false,
  message = null,
  showIconMenu = false,
  hideNormalDropdownOptions = false,
  hideCardTitle = false,
  loadingText = null,
}) => {
  const assayId = assayDetails?.id;
  const assayStatus = assayDetails?.active;

  const onPointClick = (itemIndex) => {
    const selectedResult = assayRunResults[itemIndex]
    setSelectedRun(selectedResult);
  }

  const onClose = useCallback(() => {
    setSelectedRun(null)
  }, [setSelectedRun]);

  const onStatusToggle = useCallback(() => {
    changeAssayStatus(assayId, !assayStatus);
    setTimeout(refetchAssays, 1000)
  }, [assayId, assayStatus, refetchAssays])

  if (selectedRun === null) {
    // Point Chart
    return (
      <AssayWindowPointChart
        assayName={assayName}
        assayRunResults={assayRunResults}
        assayDetails={assayDetails}
        assayStatus={assayStatus}
        message={message}
        isLoading={isLoading}
        showDetailsFor={showDetailsFor}
        onPointClick={onPointClick}
        onStatusToggle={onStatusToggle}
        showIconMenu={showIconMenu}
        hideNormalDropdownOptions={hideNormalDropdownOptions}
        hideCardTitle={hideCardTitle}
        loadingText={loadingText}
      />
    )
  }

  // Bar Chart with selected run data
  return (
    <AssayResultBarChart
      assayName={assayName}
      assayRunResult={selectedRun}
      assayDetails={assayDetails}
      assayStatus={assayStatus}
      message={message}
      isLoading={isLoading}
      showDetailsFor={showDetailsFor}
      onClose={onClose}
      onStatusToggle={onStatusToggle}
      showIconMenu={showIconMenu}
      hideNormalDropdownOptions={hideNormalDropdownOptions}
      hideCardTitle={hideCardTitle}
    />
  )
};

export default ModalAssayChart;
